import { postAuthenticatedAPI } from "@/lib/api";
import type {
  RevenueIntelligenceChatChartSpec,
  RevenueIntelligenceChatChartType,
  RevenueIntelligenceChatRegistrationValue,
  RevenueIntelligenceChatScopeAction,
} from "../components/RevenueIntelligenceChatContext";

export type RevenueIntelligenceSemanticPlan = {
  version: "1";
  intent:
    | "answer"
    | "chart"
    | "modify_chart"
    | "capabilities"
    | "save_analysis"
    | "run_saved_analysis"
    | "create_brief";
  operation:
    | "value"
    | "definition"
    | "source"
    | "evidence"
    | "explain"
    | "recommend"
    | "rank"
    | "list"
    | "compare"
    | "group"
    | "visualize"
    | "capabilities";
  contractId: string | null;
  metricIds: string[];
  insightId: string | null;
  recordSetId: string | null;
  recordQuery: {
    scope: "record_set" | "active_result" | "parent_result";
    selectFields: string[];
    filters: Array<{
      field: string;
      operator: "equals" | "in";
      values: string[];
    }>;
    orderBy: {
      field: string;
      direction: "asc" | "desc";
    } | null;
    offset: number;
    limit: number;
    groupBy: string | null;
    minimumGroupSize: number;
  } | null;
  datasetId: string | null;
  filters: Array<{
    field: string;
    operator: "equals" | "in";
    values: string[];
  }>;
  chart: RevenueIntelligenceChatChartSpec | null;
  clarification: {
    needed: boolean;
    question: string | null;
  };
};

export type RevenueIntelligencePlannerPreviousPlan = {
  contractId: string | null;
  metricIds: string[];
  insightId: string | null;
  recordSetId: string | null;
  recordQuery: RevenueIntelligenceSemanticPlan["recordQuery"];
  datasetId: string | null;
  chartType: RevenueIntelligenceChatChartType | null;
};

export type RevenueIntelligencePlannerConversationTurn = {
  role: "user" | "assistant";
  content: string;
};

export type RevenueIntelligencePlannerActiveResult = {
  contractId: string;
  recordSetId: string;
  recordIds: string[];
  parentRecordIds?: string[];
  recordIdentifiers?: string[];
  parentRecordIdentifiers?: string[];
  selectedFields: string[];
  sourceQuery: NonNullable<RevenueIntelligenceSemanticPlan["recordQuery"]>;
};

export function getRevenueIntelligenceActiveResultIdentifiers(
  contracts: RevenueIntelligenceChatRegistrationValue[],
  activeResult: RevenueIntelligencePlannerActiveResult,
) {
  const recordSet = contracts
    .flatMap(contract => contract.recordSets)
    .find(item => item.recordSetId === activeResult.recordSetId);
  if (!recordSet) return { recordIdentifiers: [], parentRecordIdentifiers: [] };
  const identifierFields = recordSet.fields.filter(field => (
    field.semanticRoles?.includes("identifier")
  ));
  const recordsById = new Map(recordSet.records.map(record => [record.id, record]));
  const identifiersFor = (recordIds: string[]) => [
    ...new Set(recordIds.flatMap((recordId) => {
      const record = recordsById.get(recordId);
      if (!record) return [];
      const identifiers = identifierFields.flatMap((field) => {
        const value = record.displayValues?.[field.field] ?? record.values[field.field];
        return value === null || value === undefined ? [] : [String(value)];
      });
      return identifiers.length ? identifiers : [record.label];
    })),
  ].slice(0, 50);
  return {
    recordIdentifiers: identifiersFor(activeResult.recordIds),
    parentRecordIdentifiers: identifiersFor(activeResult.parentRecordIds ?? []),
  };
}

export function getRevenueIntelligenceParentRecordIds({
  activeResult,
  nextRecordIds,
  preserveExistingParent = false,
  scope,
}: {
  activeResult: RevenueIntelligencePlannerActiveResult | null;
  nextRecordIds: string[];
  preserveExistingParent?: boolean;
  scope: NonNullable<RevenueIntelligenceSemanticPlan["recordQuery"]>["scope"];
}) {
  if (scope === "record_set") {
    if (preserveExistingParent && activeResult) {
      return activeResult.parentRecordIds?.length
        ? activeResult.parentRecordIds
        : activeResult.recordIds;
    }
    return nextRecordIds;
  }
  if (activeResult?.parentRecordIds?.length) return activeResult.parentRecordIds;
  if (scope === "active_result" && activeResult?.recordIds.length) {
    return activeResult.recordIds;
  }
  return nextRecordIds;
}

export function doesRevenueIntelligenceQuerySelectNamedRecords(
  recordSet: RevenueIntelligenceChatRegistrationValue["recordSets"][number],
  query: NonNullable<RevenueIntelligenceSemanticPlan["recordQuery"]>,
) {
  const identifierFields = new Set(recordSet.fields.flatMap(field => (
    field.semanticRoles?.includes("identifier") ? [field.field] : []
  )));
  return query.scope === "record_set" && query.filters.some(filter => (
    identifierFields.has(filter.field)
  ));
}

export function getRevenueIntelligenceReusableRecordChartPlan(
  plan: RevenueIntelligenceSemanticPlan,
  activeResult: RevenueIntelligencePlannerActiveResult | null,
) {
  if (
    !plan.chart
    || !plan.recordQuery
    || plan.recordQuery.scope !== "active_result"
    || !activeResult?.sourceQuery
  ) {
    return plan;
  }
  return {
    ...plan,
    recordQuery: {
      ...activeResult.sourceQuery,
      selectFields: [
        ...new Set([
          ...plan.recordQuery.selectFields,
          plan.chart.xField,
          ...plan.chart.yFields,
          ...(plan.chart.groupBy ? [plan.chart.groupBy] : []),
        ]),
      ],
      groupBy: null,
      minimumGroupSize: 1,
    },
  };
}

function normalizePlannerQuestion(question: string) {
  return question.toLocaleLowerCase().trim();
}

function includesPlannerTerm(question: string, terms: string[]) {
  return terms.some(term => question.includes(term));
}

export function asksForRevenueIntelligenceEmailEvidence(question: string) {
  const normalized = normalizePlannerQuestion(question);
  return /\bvad stod(?: det)?(?: exakt)? i (?:mejlet|mailet|meddelandet)\b/u.test(normalized)
    || /\bvad stod det (?:for|för|om)\b/u.test(normalized)
    || /\b(?:visa|ge mig)(?: det)?(?: exakta?| ursprungliga?)? (?:original\s*)?(?:mejlet|mailet|meddelandet|citatet|utdraget)\b/u.test(normalized)
    || /\bwhat (?:did|does) (?:the )?(?:email|message) (?:exactly )?say\b/u.test(normalized)
    || /\bwhat did it say (?:for|about)\b/u.test(normalized)
    || /\b(?:show|give me) (?:the )?(?:exact |original )?(?:email|message|quote|excerpt)\b/u.test(normalized);
}

export function isRevenueIntelligenceChartRequest(question: string) {
  const normalized = normalizePlannerQuestion(question);
  return includesPlannerTerm(normalized, [
    "chart",
    "graph",
    "plot",
    "visualise",
    "visualize",
    "draw",
    "graf",
    "diagram",
    "visualisera",
    "rita",
  ]);
}

export function referencesRevenueIntelligenceActiveResult(question: string) {
  const normalized = normalizePlannerQuestion(question);
  return asksForRevenueIntelligenceEmailEvidence(normalized)
    || /\b(it|them|their|these|those|den|det|de|dem|deras|dessa)\b/u.test(normalized)
    || includesPlannerTerm(normalized, [
    " them",
    "their ",
    "these ",
    "those ",
    "the selected",
    " dem",
    "deras ",
    "dessa ",
    "de här",
    "de valda",
    "ovanstående",
    "what did the email say",
    "what did the message say",
    "what should we do",
    "vad stod i mejlet",
    "vad stod i mailet",
    "vad stod i meddelandet",
    "vad bör vi göra",
    "vad ska vi göra",
    ]);
}

function asksForHistoricalMovement(question: string) {
  return includesPlannerTerm(question, [
    "over time",
    "since the start",
    "since year",
    "year to date",
    "ytd",
    "trend",
    "developed",
    "changed",
    "movement",
    "över tid",
    "sedan årsskift",
    "sen årsskift",
    "utveckl",
    "förändr",
    "trend",
  ]);
}

function asksSinceYearStart(question: string) {
  return includesPlannerTerm(question, [
    "since the start of the year",
    "since year start",
    "year to date",
    "ytd",
    "sedan årsskift",
    "sen årsskift",
    "från årsskift",
    "hittills i år",
  ]);
}

function getRequestedPeriodScopeActionId(question: string) {
  const normalized = normalizePlannerQuestion(question);
  const matches = [
    {
      actionId: "state_of_sales.period.this_year",
      matches: asksSinceYearStart(normalized) || includesPlannerTerm(normalized, [
        "this year", "current year", "i år",
      ]),
    },
    {
      actionId: "state_of_sales.period.prev_quarter",
      matches: includesPlannerTerm(normalized, [
        "previous quarter", "last quarter", "förra kvartalet", "föregående kvartal",
      ]),
    },
    {
      actionId: "state_of_sales.period.next_6_months",
      matches: includesPlannerTerm(normalized, [
        "next six months", "next 6 months", "kommande sex månader",
        "kommande 6 månader", "nästa sex månader", "nästa 6 månader",
      ]),
    },
    {
      actionId: "state_of_sales.period.next_quarter",
      matches: includesPlannerTerm(normalized, [
        "next quarter", "nästa kvartal", "kommande kvartal",
      ]),
    },
    {
      actionId: "state_of_sales.period.this_quarter",
      matches: includesPlannerTerm(normalized, [
        "this quarter", "current quarter", "detta kvartal", "innevarande kvartal",
      ]),
    },
  ].filter(match => match.matches);
  return matches.length === 1 ? matches[0]?.actionId ?? null : null;
}

export function getRevenueIntelligenceChartScopeActionId(question: string) {
  if (!isRevenueIntelligenceChartRequest(question)) return null;
  return getRequestedPeriodScopeActionId(question);
}

export function getRevenueIntelligenceRequestedScopeActions(
  question: string,
  actions: RevenueIntelligenceChatScopeAction[],
) {
  const normalized = normalizePlannerQuestion(question);
  const requestedPeriodActionId = getRequestedPeriodScopeActionId(normalized);
  const selected = new Map<string, RevenueIntelligenceChatScopeAction>();

  for (const action of actions) {
    if (action.field === "period") {
      if (action.actionId === requestedPeriodActionId) {
        selected.set("period", action);
      }
      continue;
    }
    const normalizedLabel = normalizePlannerQuestion(action.label);
    const aliases = (action.aliases ?? [])
      .map(normalizePlannerQuestion)
      .filter(alias => alias.length > 2);
    const hasFieldCue = action.field === "pipeline"
      ? includesPlannerTerm(normalized, ["pipeline"])
      : action.field === "team"
        ? includesPlannerTerm(normalized, ["team", "teamet"])
        : action.field === "owner"
          ? includesPlannerTerm(normalized, [
              "owner", "sales rep", "seller", "säljare", "ägare",
            ])
          : false;
    const directLabelMatch = normalized.includes(normalizedLabel);
    const aliasMatch = aliases.some(alias => normalized.includes(alias));
    if (
      directLabelMatch
      || (aliasMatch && (action.field === "owner" || hasFieldCue))
    ) {
      selected.set(action.field ?? action.actionId, action);
    }
  }

  return [...selected.values()];
}

function datasetCoversYearStart(
  dataset: RevenueIntelligenceChatRegistrationValue["datasets"][number],
) {
  const dateFields = dataset.dimensions
    .filter(dimension => dimension.type === "date")
    .map(dimension => dimension.field);
  const isoMonths = dataset.rows.flatMap(row => (
    dateFields.flatMap(field => {
      const value = String(row[field] ?? "");
      return /^\d{4}-\d{2}/.test(value) ? [value.slice(0, 7)] : [];
    })
  ));
  const latestYear = isoMonths
    .map(value => value.slice(0, 4))
    .toSorted()
    .at(-1);
  return Boolean(latestYear && isoMonths.includes(`${latestYear}-01`));
}

function scoreDatasetForQuestion(
  question: string,
  contract: RevenueIntelligenceChatRegistrationValue,
  dataset: RevenueIntelligenceChatRegistrationValue["datasets"][number],
) {
  const searchable = [
    contract.contractId,
    contract.scope.pageLabel,
    ...contract.topics,
    dataset.datasetId,
    dataset.label,
    dataset.description,
  ].join(" ").toLocaleLowerCase();
  let score = 0;

  const topicGroups = [
    ["forecast", "prognos", "target", "mål"],
    ["pipeline risk", "risk exposure", "riskexponering", "high-risk", "högrisk"],
    ["pipeline stage", "pipeline by stage", "pipeline per steg", "pipeline per fas"],
    ["deal risk", "deal map", "bubble", "bubbla"],
  ];
  for (const group of topicGroups) {
    if (
      includesPlannerTerm(question, group)
      && includesPlannerTerm(searchable, group)
    ) {
      score += 20;
    }
  }

  for (const topic of contract.topics) {
    if (question.includes(topic.toLocaleLowerCase())) score += 4;
  }
  if (question.includes(dataset.label.toLocaleLowerCase())) score += 12;
  if (
    includesPlannerTerm(question, ["bubble", "bubbla", "deal risk"])
    && dataset.allowedChartTypes.includes("scatter")
  ) {
    score += 16;
  }
  if (
    includesPlannerTerm(question, ["severity", "risk level", "risknivå"])
    && dataset.datasetId.includes("severity")
  ) {
    score += 16;
  }
  return score;
}

export function buildRevenueIntelligenceDeterministicChartPlan(
  question: string,
  contracts: RevenueIntelligenceChatRegistrationValue[],
): {
  periodLabel: string | null;
  plan: RevenueIntelligenceSemanticPlan;
} | null {
  if (!isRevenueIntelligenceChartRequest(question)) return null;

  const normalized = normalizePlannerQuestion(question);
  const requiresDateSeries = asksForHistoricalMovement(normalized);
  const requiresYearStart = asksSinceYearStart(normalized);
  const candidates = contracts.flatMap(contract => (
    contract.datasets.map(dataset => ({
      contract,
      dataset,
      score: scoreDatasetForQuestion(normalized, contract, dataset),
    }))
  )).filter(({ dataset }) => (
    (
      !requiresDateSeries
      || dataset.dimensions.some(dimension => dimension.type === "date")
    )
    && (!requiresYearStart || datasetCoversYearStart(dataset))
  ));
  if (!candidates.length) return null;

  const ranked = candidates.toSorted((a, b) => b.score - a.score);
  const selected = ranked[0];
  if (!selected) return null;
  if (
    ranked.length > 1
    && selected.score === 0
    && ranked[1]?.score === 0
  ) {
    return null;
  }

  const chart = selected.dataset.defaultChart;
  const metricIds = [
    ...new Set(
      selected.dataset.measures
        .filter(measure => chart.yFields.includes(measure.field))
        .map(measure => measure.metricId)
        .filter((metricId): metricId is string => Boolean(metricId)),
    ),
  ];
  return {
    periodLabel: selected.contract.scope.periodLabel ?? null,
    plan: {
      version: "1",
      intent: "chart",
      operation: "visualize",
      contractId: selected.contract.contractId,
      metricIds,
      insightId: null,
      recordSetId: null,
      recordQuery: null,
      datasetId: selected.dataset.datasetId,
      filters: [],
      chart: { ...chart, yFields: [...chart.yFields] },
      clarification: {
        needed: false,
        question: null,
      },
    },
  };
}

export function getRevenueIntelligenceChartLimitation(
  question: string,
  contracts: RevenueIntelligenceChatRegistrationValue[],
) {
  if (!isRevenueIntelligenceChartRequest(question)) return null;
  const normalized = normalizePlannerQuestion(question);
  if (asksSinceYearStart(normalized)) {
    const hasYearStartSeries = contracts.some(contract => (
      contract.datasets.some(dataset => (
        dataset.dimensions.some(dimension => dimension.type === "date")
        && datasetCoversYearStart(dataset)
      ))
    ));
    if (!hasYearStartSeries) {
      return {
        answer: [
          "The approved time series in this view does not cover the period since the start of the year.",
          "",
          "Change Period to This year, then ask for the chart again.",
        ].join("\n"),
        followUpQuestions: ["Draw Forecast for the current period"],
      };
    }
  }
  const asksForRisk = includesPlannerTerm(normalized, [
    "risk",
    "riskexponering",
    "risk exposure",
  ]);
  if (asksForRisk && asksForHistoricalMovement(normalized)) {
    const hasRiskTimeSeries = contracts.some(contract => (
      includesPlannerTerm(
        [contract.contractId, ...contract.topics].join(" ").toLocaleLowerCase(),
        ["risk", "riskexponering", "risk exposure"],
      )
      && contract.datasets.some(dataset => (
        dataset.dimensions.some(dimension => dimension.type === "date")
      ))
    ));
    if (!hasRiskTimeSeries) {
      return {
        answer: [
          "I can’t draw how risk exposure has developed over time from this view yet.",
          "",
          "The approved data shows current risk exposure, but there is no comparable historical risk-exposure series.",
        ].join("\n"),
        followUpQuestions: [
          "Draw current Pipeline Risk by severity",
          "What needs management attention?",
        ],
      };
    }
  }
  return {
    answer: [
      "I can’t draw that chart from the approved data available in this view.",
      "",
      "Try naming Forecast, Pipeline Risk, or pipeline stage, or change the page period before asking again.",
    ].join("\n"),
    followUpQuestions: ["Draw Forecast over time", "Draw Pipeline Risk by severity"],
  };
}

export function buildRevenueIntelligencePlannerCatalogue(
  contracts: RevenueIntelligenceChatRegistrationValue[],
) {
  return contracts.map(contract => ({
    contractId: contract.contractId,
    label: contract.metrics[0]?.sourceLabel
      ?? contract.insights[0]?.sourceLabel
      ?? contract.contractId,
    topics: contract.topics,
    metricIds: contract.metrics.map(metric => metric.metricId),
    insightIds: contract.insights.map(insight => insight.insightId),
    recordSets: contract.recordSets.map(recordSet => ({
      recordSetId: recordSet.recordSetId,
      label: recordSet.label,
      aliases: recordSet.aliases,
      fields: recordSet.fields.map(field => ({
        field: field.field,
        label: field.label,
        aliases: field.aliases,
        semanticRoles: field.semanticRoles ?? [],
        allowedFilterValues: field.allowedFilterValues ?? [],
        type: field.type,
        metricId: field.metricId ?? null,
        sortable: field.sortable,
        filterable: field.filterable ?? false,
      })),
      defaultSort: recordSet.defaultSort,
    })),
    scope: {
      pageLabel: contract.scope.pageLabel,
      periodLabel: contract.scope.periodLabel ?? null,
      filtersLabel: contract.scope.filtersLabel ?? null,
    },
    datasets: contract.datasets.map(dataset => ({
      datasetId: dataset.datasetId,
      dimensions: dataset.dimensions.map(field => field.field),
      measures: dataset.measures.map(field => field.field),
      allowedChartTypes: dataset.allowedChartTypes,
    })),
    // Filters stay closed until a contract declares reviewed, non-sensitive
    // values. Field names alone are not enough to validate model-produced
    // filter values without exposing raw dataset rows to the planner.
    allowedFilterFields: [],
  }));
}

export async function requestRevenueIntelligenceSemanticPlan({
  accessToken,
  companyId,
  contracts,
  conversation,
  conversationId,
  previousPlan,
  activeResult,
  question,
  signal,
}: {
  accessToken: string;
  companyId: string;
  contracts: RevenueIntelligenceChatRegistrationValue[];
  conversation: RevenueIntelligencePlannerConversationTurn[];
  conversationId: string;
  previousPlan: RevenueIntelligencePlannerPreviousPlan | null;
  activeResult: RevenueIntelligencePlannerActiveResult | null;
  question: string;
  signal?: AbortSignal;
}) {
  const activeResultIdentifiers = activeResult
    ? getRevenueIntelligenceActiveResultIdentifiers(contracts, activeResult)
    : null;
  return postAuthenticatedAPI<{
    plan: RevenueIntelligenceSemanticPlan;
    requestId: string;
    usage: {
      cachedInputTokens: number;
      inputTokens: number;
      model: string;
      outputTokens: number;
    };
  }>(
    "/revenue-intelligence/chat/plan",
    {
      question,
      companyId,
      conversationId,
      conversation: conversation.slice(-6),
      previousPlan,
      activeResult: activeResult
        ? {
            contractId: activeResult.contractId,
            recordSetId: activeResult.recordSetId,
            recordCount: activeResult.recordIds.length,
            parentRecordCount: activeResult.parentRecordIds?.length ?? 0,
            recordIdentifiers: activeResultIdentifiers?.recordIdentifiers ?? [],
            parentRecordIdentifiers: activeResultIdentifiers?.parentRecordIdentifiers ?? [],
            selectedFields: activeResult.selectedFields,
            sourceQuery: activeResult.sourceQuery,
          }
        : null,
      contracts: buildRevenueIntelligencePlannerCatalogue(contracts),
    },
    accessToken,
    signal,
  );
}

export async function requestRevenueIntelligenceAnswerComposition({
  accessToken,
  approvedAnswer,
  companyId,
  conversationId,
  question,
  signal,
}: {
  accessToken: string;
  approvedAnswer: string;
  companyId: string;
  conversationId: string;
  question: string;
  signal?: AbortSignal;
}) {
  return postAuthenticatedAPI<{
    answer: string;
    requestId: string;
    usage: {
      cachedInputTokens: number;
      inputTokens: number;
      model: string;
      outputTokens: number;
    };
  }>(
    "/revenue-intelligence/chat/compose",
    {
      approvedAnswer,
      companyId,
      conversationId,
      question,
    },
    accessToken,
    signal,
  );
}

export function toPlannerPreviousPlan(
  plan: RevenueIntelligenceSemanticPlan,
): RevenueIntelligencePlannerPreviousPlan {
  return {
    contractId: plan.contractId,
    metricIds: plan.metricIds,
    insightId: plan.insightId,
    recordSetId: plan.recordSetId,
    recordQuery: plan.recordQuery,
    datasetId: plan.datasetId,
    chartType: plan.chart?.type ?? null,
  };
}
